import { useOutletContext } from "react-router-dom";
import type { HubOutletContext } from "../../EnterpriseHub";
import { Alert, Box, SimpleGrid, Text } from "@mantine/core";
import { ProductBlock } from "../../../components/ProductBlock";
import { BrandedSkeleton } from "../../../components/BrandedSkeleton";
import { useStartTrial } from "../../../hooks/useStartTrial";
import { useAccessRequests } from "../../../hooks/useAccessRequests";
import { InlineNotice } from "../../../components/InlineNotice";
import { ProvisioningNotice } from "../../../components/ProvisioningNotice";
import { cardSetupState } from "../../../lib/provisioning";
import { productHref } from "../../../lib/productHref";
import { productCatalogue } from "../../../catalogue/products";
import { accessStateFor } from "../../../lib/accessState";
import { relativeTimeFrom } from "../../../lib/relativeTime";

// Plain module fn (outside render) so the Date.now() read stays lint-pure.
function trialDaysLeft(trialEndsAt: string): number {
  return Math.max(0, Math.ceil((new Date(trialEndsAt).getTime() - Date.now()) / 86400000));
}

export function MultiAppScenario() {
  const { state, sections, refetch, isOrgAdmin, orgSlug, roles, setup, trialIntentBusy, trialStarted } = useOutletContext<HubOutletContext>();
  const status = state.kind;
  const trial = useStartTrial({ onStarted: (productKey) => { setup.watch(productKey); refetch(); trialStarted(productKey); } });
  // Only a member of an organisation that enforces roles somewhere has requests to read.
  const needsRequests = !isOrgAdmin && (sections?.active.some(([, e]) => e.roleBasedAccess) ?? false);
  const requests = useAccessRequests({ enabled: needsRequests, roles, onGranted: refetch });
  const setupProduct = productCatalogue.find((p) => p.key === setup.productKey);

  // The server decides trialability (offer exists, not active, once-per-org honoured) — the
  // hub only draws what /me says.
  const trialable = (e?: { trialAvailable?: boolean }) => isOrgAdmin && e?.trialAvailable === true;
  const knownRoles = requests.freshRoles ?? roles;

  return (
    <>
      {status === 'loading' ? <BrandedSkeleton /> : null}

      {status === 'error' && <ErrorPanel />}

      {trial.error && <InlineNotice tone="error" message={trial.error} onClose={trial.clear} />}
      {requests.error && <InlineNotice tone="error" message={requests.error} onClose={requests.clear} />}
      {requests.success && <InlineNotice tone="success" message={requests.success} onClose={requests.clear} />}
      {setupProduct && setup.state !== 'idle' ? (
        <ProvisioningNotice product={setupProduct} state={setup.state} stages={setup.stages} onClose={setup.dismiss} />
      ) : (
        trial.success && <InlineNotice tone="success" message={trial.success} onClose={trial.clear} />
      )}

      {sections && (
        <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="lg">
          {[...sections.active, ...sections.available].map(([product, e]) => {
            const access = accessStateFor({
              productKey: product.key, entitlement: e, roles: knownRoles, openRequests: requests.openProducts, isOrgAdmin,
            });
            const common = { product, entitlement: e, setupState: cardSetupState(product.key, setup) };
            if (requests.loading && (access === 'request' || access === 'requested')) {
              return <ProductBlock key={product.key} {...common} isLocked note="Checking your access…" />;
            }
            if (access === 'request') {
              return (
                <ProductBlock key={product.key} {...common} isLocked ctaLabel="Request access"
                  onCta={() => void requests.request(product.key, product.name)}
                  ctaBusy={requests.busyProducts.has(product.key)} />
              );
            }
            if (access === 'requested') {
              return (
                <ProductBlock key={product.key} {...common} isLocked badge={{ title: 'Requested', color: 'var(--accent-text)' }}
                  note={`Waiting for an admin · requested ${relativeTimeFrom(requests.requestedAt.get(product.key))}`} />
              );
            }
            if (access === 'adminNoRole') {
              return (
                <ProductBlock key={product.key} {...common} isLocked
                  note={`You don't hold a ${product.name} role yet. Give yourself one on the Users page.`} />
              );
            }
            if (access === 'notOnPlan' && !isOrgAdmin) {
              return <ProductBlock key={product.key} {...common} isLocked ctaLabel="Not on your organisation's plan" />;
            }
            let badge: { title: string; color: string } | undefined;
            if (e?.plan === 'trial' && e?.trialEndsAt) {
              const days = trialDaysLeft(e.trialEndsAt);
              badge = { title: `Trial · ends in ${days}d`, color: 'var(--accent-text)' };
            }
            return (
              <ProductBlock key={product.key} {...common}
                ctaLabel={trialable(e) ? `Try ${product.name}` : undefined}
                onCta={trialable(e) ? () => trial.start(product.key) : undefined}
                ctaBusy={trialable(e) ? trial.busyProducts.has(product.key) : undefined}
                ctaDisabled={trialable(e) ? trialIntentBusy : undefined}
                badge={badge}
                href={productHref(product.path, orgSlug, globalThis.location.hostname)}
              />
            );
          })}
        </SimpleGrid>
      )}

      {status === 'ready' && sections?.active.length === 0 && sections?.available.length === 0 && (
        <NoProductsPanel />
      )}
    </>
  );
}


function ErrorPanel() {
  return (
    <Alert
      color="var(--status-danger)"
      variant="light"
      title="Unable to load your products"
      style={{
        borderColor: 'var(--status-danger-background)',
        '--alert-bg': 'color-mix(in srgb, var(--alert-color), var(--bg-canvas) 92%)',
      }}
    >
      <Text c={'var(--text-muted)'} size="xs">
        We couldn't reach the Cloudhouse account service. Please refresh in a moment.
      </Text>
    </Alert>
  );
}

function NoProductsPanel() {
  return (
    <Box bg={'var(--bg-surface)'} p={'lg'} bdrs={'md'} bd={'1px solid var(--border-default)'} >
      <Text size="md" c={'var(--text-subtle)'}>
        No Cloudhouse products are configured for your organisation yet. Talk to your account manager to get started.
      </Text>
    </Box>
  );
}
